import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Edit2, Trash2, Search, X, Check, Filter } from 'lucide-react';
import { getModels, getCategories, addModel, updateModel, deleteModel } from '../../services/adminStorage';

export default function ModelsManager() {
  const [models, setModels] = useState(getModels());
  const [categories, setCategories] = useState(getCategories());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('todos');

  // Modal de formulário (Novo ou Editar)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    categorySlug: '',
    description: '',
    tags: '',
    accentColor: '#2563eb',
    features: '',
    demoUrl: ''
  });

  useEffect(() => {
    const handleUpdate = () => {
      setModels(getModels());
      setCategories(getCategories());
    };
    window.addEventListener('suzume_data_updated', handleUpdate);
    return () => window.removeEventListener('suzume_data_updated', handleUpdate);
  }, []);

  const openNewModelModal = () => {
    setEditingModel(null);
    setFormData({
      name: '',
      category: categories[1] ? categories[1].label : 'Artesanato & Handcrafted',
      categorySlug: categories[1] ? categories[1].id : 'artesanato',
      description: '',
      tags: 'Responsivo, Alta Conversão, WhatsApp',
      accentColor: '#2563eb',
      features: 'Design Otimizado\nAtendimento via WhatsApp\nGaleria de Fotos\nPerguntas Frequentes',
      demoUrl: ''
    });
    setIsModalOpen(true);
  };

  const openEditModal = (model) => {
    setEditingModel(model);
    setFormData({
      name: model.name || '',
      category: model.category || '',
      categorySlug: model.categorySlug || 'geral',
      description: model.description || '',
      tags: Array.isArray(model.tags) ? model.tags.join(', ') : (model.tags || ''),
      accentColor: model.accentColor || '#2563eb',
      features: Array.isArray(model.features) ? model.features.join('\n') : (model.features || ''),
      demoUrl: model.demoUrl || model.demo_url || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Tem certeza que deseja excluir o modelo "${name}"?`)) {
      const updated = await deleteModel(id);
      setModels(updated);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Encontrar categorySlug correto
    const matchedCat = categories.find(c => c.label === formData.category);
    const categorySlug = matchedCat ? matchedCat.id : (formData.categorySlug || 'geral');

    const payload = {
      ...formData,
      categorySlug
    };

    let updated;
    if (editingModel) {
      updated = await updateModel(editingModel.id, payload);
    } else {
      updated = await addModel(payload);
    }
    setModels(updated);

    setIsModalOpen(false);
  };

  // Filtragem
  const filteredModels = models.filter((m) => {
    const matchCat = selectedCategory === 'todos' || m.categorySlug === selectedCategory;
    const q = searchQuery.toLowerCase().trim();
    const matchQ = !q || m.name.toLowerCase().includes(q) || m.category.toLowerCase().includes(q) || m.description.toLowerCase().includes(q);
    return matchCat && matchQ;
  });

  return (
    <div className="animate-fade-in">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '1.75rem', color: 'var(--text-title)', fontWeight: 800 }}>
            Gerenciar Modelos do Catálogo
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>
            Adicione, edite ou remova landing pages do acervo da Suzume Technologies.
          </p>
        </div>

        <button className="btn btn-primary" onClick={openNewModelModal}>
          <Plus size={18} />
          Cadastrar Novo Modelo
        </button>
      </div>

      {/* Filtros de Busca */}
      <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '1.25rem', marginBottom: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '260px' }}>
          <Search size={18} color="var(--text-muted)" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)' }} />
          <input 
            type="text"
            placeholder="Pesquisar por nome do modelo ou descrição..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-control"
            style={{ paddingLeft: '2.5rem' }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Filter size={16} color="var(--text-muted)" />
          <select 
            value={selectedCategory} 
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="form-control"
            style={{ minWidth: '180px' }}
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Lista de Modelos */}
      <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ background: 'var(--bg-surface-alt)', borderBottom: '1px solid var(--border-color)', color: 'var(--text-title)' }}>
              <th style={{ padding: '1rem' }}>Modelo</th>
              <th style={{ padding: '1rem' }}>Nicho / Categoria</th>
              <th style={{ padding: '1rem' }}>Tags</th>
              <th style={{ padding: '1rem', textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredModels.length > 0 ? (
              filteredModels.map((m) => (
                <tr key={m.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontWeight: 700, color: 'var(--text-title)', fontSize: '0.95rem' }}>{m.name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', maxWidth: '380px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {m.description}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span className="badge badge-category">{m.category}</span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                      {m.tags && m.tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} style={{ background: '#f1f5f9', padding: '0.15rem 0.45rem', borderRadius: '4px', fontSize: '0.75rem', color: '#475569' }}>
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem' }}>
                      <button 
                        className="btn btn-secondary btn-sm" 
                        onClick={() => openEditModal(m)}
                        title="Editar Modelo"
                      >
                        <Edit2 size={15} /> Editar
                      </button>
                      <button 
                        className="btn btn-sm" 
                        onClick={() => handleDelete(m.id, m.name)}
                        style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }}
                        title="Excluir Modelo"
                      >
                        <Trash2 size={15} /> Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  Nenhum modelo encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Formulário (Novo / Editar) */}
      {isModalOpen && createPortal(
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content animate-fade-in" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '650px' }}>
            <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}>
              <X size={20} />
            </button>

            <div style={{ padding: '1.75rem 2rem 1rem', borderBottom: '1px solid var(--border-color)' }}>
              <h3 style={{ fontSize: '1.4rem', color: 'var(--text-title)' }}>
                {editingModel ? 'Editar Modelo' : 'Cadastrar Novo Modelo'}
              </h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                Preencha as informações da landing page para exibição no catálogo público.
              </p>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '1.5rem 2rem' }}>
              <div className="form-group">
                <label className="form-label">Nome da Landing Page *</label>
                <input 
                  type="text"
                  required
                  placeholder="Ex: AutoFix Mecânica Especializada"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Nicho / Categoria *</label>
                <select 
                  className="form-control"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  {categories.filter(c => c.id !== 'todos').map((c) => (
                    <option key={c.id} value={c.label}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Descrição Curta *</label>
                <textarea 
                  required
                  rows="3"
                  placeholder="Descrição sobre o público-alvo e estilo do modelo..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-control"
                ></textarea>
              </div>

              <div className="form-group">
                <label className="form-label">Tags (separadas por vírgula)</label>
                <input 
                  type="text"
                  placeholder="Catálogo Visual, Pedido WhatsApp, Depoimentos"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Recursos Inclusos (1 por linha)</label>
                <textarea 
                  rows="4"
                  placeholder="Design Otimizado&#10;Atendimento via WhatsApp&#10;Galeria com Zoom"
                  value={formData.features}
                  onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                  className="form-control"
                ></textarea>
              </div>

              <div className="form-group">
                <label className="form-label">URL da Demonstração ao Vivo (Pasta /models/)</label>
                <input 
                  type="text"
                  placeholder="Ex: /models/artesanal/ ou /models/artesanal/index.html"
                  value={formData.demoUrl}
                  onChange={(e) => setFormData({ ...formData, demoUrl: e.target.value })}
                  className="form-control"
                />
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem', display: 'block' }}>
                  💡 Informe a pasta buildada salva dentro de <code>public/models/</code> (Ex: <code>/models/artesanal/</code>) ou uma URL externa.
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  <Check size={18} />
                  {editingModel ? 'Salvar Alterações' : 'Cadastrar Modelo'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}
